// Shared types for AI Agent Waterfall System

export type AgentType =
  | "orchestrator"
  | "researcher"
  | "analyzer"
  | "writer"
  | "executor";

export type WorkflowStatus =
  | "idle"
  | "running"
  | "paused"
  | "completed"
  | "failed"
  | "cancelled";

export type AgentStatus =
  | "idle"
  | "assigned"
  | "processing"
  | "complete"
  | "error";

export type EventType =
  | "message"
  | "toolCall"
  | "toolResult"
  | "statusChange"
  | "error";

export interface WorkflowConfig {
  name: string;
  description: string;
  objective: string;
  maxDuration?: number;
  agentTypes?: AgentType[];
  parallelExecution?: boolean;
  autoStart?: boolean;
}

export interface Workflow {
  id: string;
  name: string;
  description: string;
  objective: string;
  status: WorkflowStatus;
  config: WorkflowConfig;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  userId: string;
  totalEvents: number;
  duration?: number;
}

export interface Agent {
  id: string;
  workflowId: string;
  type: AgentType;
  objective: string;
  status: AgentStatus;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, unknown>;
}

export interface Event {
  id: string;
  workflowId: string;
  agentId: string;
  type: EventType;
  timestamp: number;
  content: string;
  status: string;
  toolName?: string;
  metadata?: Record<string, unknown>;
  duration?: number;
  parentEventId?: string;
}

export interface WorkflowSummary {
  workflow: Workflow;
  agents: Agent[];
  eventCount: number;
  lastActivity: string;
}

// WebSocket message types
export type WebSocketMessageType =
  | "workflow.started"
  | "workflow.completed"
  | "workflow.failed"
  | "workflow.cancelled"
  | "workflow.paused"
  | "workflow.resumed"
  | "workflow.state"
  | "agent.spawned"
  | "agent.completed"
  | "agent.failed"
  | "event.created"
  | "error.occurred"
  | "heartbeat"
  | "subscribe"
  | "unsubscribe"
  | "connection.established";

export interface WebSocketMessage {
  type: WebSocketMessageType;
  data?: unknown;
  timestamp: number;
  workflowId?: string;
  error?: string;
}

export interface WorkflowCreateRequest {
  name: string;
  description: string;
  objective: string;
  config?: Partial<WorkflowConfig>;
}

export interface WorkflowStartRequest {
  workflowId: string;
  userId: string;
}

export interface APIResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: number;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface WorkflowListResponse {
  workflows: WorkflowSummary[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface EventListResponse {
  events: Event[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// Agent-specific objective templates
export const AGENT_OBJECTIVES: Record<AgentType, string> = {
  orchestrator: "Coordinate all agents and manage workflow execution",
  researcher:
    "Gather comprehensive data from multiple sources and validate information accuracy",
  analyzer:
    "Identify patterns, trends, and insights using statistical analysis and data processing",
  writer:
    "Create professional documents, reports, and content with proper structure and clarity",
  executor:
    "Generate visualizations, charts, and interactive elements to present findings",
};

// Color schemes for visualization
export const AGENT_COLORS: Record<AgentType, string> = {
  orchestrator: "#8b5cf6",
  researcher: "#06b6d4",
  analyzer: "#10b981",
  writer: "#f59e0b",
  executor: "#ef4444",
};

export const EVENT_COLORS: Record<EventType, string> = {
  message: "#6b7280",
  toolCall: "#3b82f6",
  toolResult: "#10b981",
  statusChange: "#8b5cf6",
  error: "#ef4444",
};

// Validation helpers
export function isValidAgentType(type: string): type is AgentType {
  return [
    "orchestrator",
    "researcher",
    "analyzer",
    "writer",
    "executor",
  ].includes(type);
}

export function isValidWorkflowStatus(
  status: string,
): status is WorkflowStatus {
  return [
    "idle",
    "running",
    "paused",
    "completed",
    "failed",
    "cancelled",
  ].includes(status);
}

export function isValidEventType(type: string): type is EventType {
  return [
    "message",
    "toolCall",
    "toolResult",
    "statusChange",
    "error",
  ].includes(type);
}

// Error types
export class WorkflowError extends Error {
  constructor(
    message: string,
    public code: string,
    public workflowId?: string,
    public agentId?: string,
  ) {
    super(message);
    this.name = "WorkflowError";
  }
}

export class AgentError extends Error {
  constructor(
    message: string,
    public code: string,
    public agentId: string,
    public workflowId?: string,
  ) {
    super(message);
    this.name = "AgentError";
  }
}

// Constants
export const MAX_WORKFLOW_DURATION = 300; // 5 minutes in seconds
export const MAX_EVENTS_PER_WORKFLOW = 1000;
export const MAX_CONCURRENT_WORKFLOWS_PER_USER = 10;
export const WEBSOCKET_HEARTBEAT_INTERVAL = 30000; // 30 seconds
export const EVENT_BATCH_SIZE = 10;
export const DEFAULT_PAGE_SIZE = 20;

// Cloudflare Worker Environment Types
export interface Env {
  WORKFLOWS_KV: KVNamespace;
  EVENTS_KV: KVNamespace;
  AGENTS_KV: KVNamespace;
  CLAUDE_API_KEY: string;
  OPENAI_API_KEY?: string;
  WORKFLOW_ORCHESTRATOR: DurableObjectNamespace;
  AGENT_WORKER: DurableObjectNamespace;
  DB?: D1Database;
  AI?: Ai;
  VECTORIZE?: VectorizeIndex;
  HYPERDRIVE?: Hyperdrive;
  R2_BUCKET?: R2Bucket;
  QUEUE?: Queue;
}

// Claude API Types
export interface ClaudeMessage {
  role: "user" | "assistant" | "system";
  content: string | ClaudeContent[];
}

export interface ClaudeContent {
  type: "text" | "tool_use" | "tool_result";
  text?: string;
  id?: string;
  name?: string;
  input?: Record<string, unknown>;
  content?: string | unknown[];
  is_error?: boolean;
}

export interface ClaudeToolDefinition {
  name: string;
  description: string;
  input_schema: {
    type: "object";
    properties: Record<string, unknown>;
    required?: string[];
  };
}

export interface ClaudeAPIRequest {
  model: string;
  max_tokens: number;
  temperature?: number;
  messages: ClaudeMessage[];
  tools?: ClaudeToolDefinition[];
  tool_choice?: "auto" | "any" | { type: "tool"; name: string };
  stream?: boolean;
}

export interface ClaudeAPIResponse {
  id: string;
  type: "message";
  role: "assistant";
  content: ClaudeContent[];
  model: string;
  stop_reason: "end_turn" | "max_tokens" | "stop_sequence" | "tool_use" | null;
  stop_sequence: string | null;
  usage: {
    input_tokens: number;
    output_tokens: number;
  };
}

// Tool execution types
export interface ToolCall {
  id: string;
  name: string;
  input: Record<string, unknown>;
}

export interface ToolResult {
  id: string;
  content: string;
  is_error?: boolean;
  metadata?: Record<string, unknown>;
}

// Agent task input types
export interface OrchestrationInput {
  objective: string;
  required_agents?: string[];
  constraints?: string[];
  priority?: "low" | "medium" | "high";
}

export interface ResearchInput {
  task: string;
  sources?: string[];
  depth?: "shallow" | "deep";
  focus_areas?: string[];
}

export interface AnalysisInput {
  data: unknown;
  analysis_type: "statistical" | "trend" | "pattern" | "comparative";
  metrics?: string[];
  timeframe?: string;
}

export interface WritingInput {
  content: unknown;
  template: "executive" | "technical" | "marketing" | "academic";
  tone?: "formal" | "casual" | "persuasive";
  length?: "brief" | "detailed" | "comprehensive";
}

export interface ExecutionInput {
  task: string;
  format: "chart" | "dashboard" | "report" | "visualization";
  data?: unknown;
  styling?: Record<string, unknown>;
}
